import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { FaEnvelope, FaComment, FaSearch, FaTimes } from "react-icons/fa";
import { FaTelegram } from "react-icons/fa";
import { useChannels } from "@/api/channel";
import { ChannelApiResponse, ChannelEmail, ChannelTelegram, ChannelWebhook, ChannelSlack } from "@/models/channel";

/**
 * ChannelsSection component - displays notification channels in the sidebar.
 * Shows all channels (email, telegram, etc.) with search functionality.
 */
const ChannelsSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: channelsRaw = [] } = useChannels();

  // Transform channels for display
  const channels = useMemo(() => {
    return channelsRaw.map((e) => {
      let value = "";
      switch (e.doc_type) {
        case "mail":
          value = (e as ChannelEmail).email;
          break;
        case "telegram":
          value = (e as ChannelTelegram).chat_id;
          break;
        case "webhook":
          value = (e as ChannelWebhook).url;
          break;
        case "slack":
          value = (e as ChannelSlack).webhook_url;
          break;
        case "in_app":
          value = "In-App Notification";
          break;
      }
      return { ...e, value };
    });
  }, [channelsRaw]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter channels by search
  const filteredChannels = useMemo(() => {
    if (!searchQuery) return channels;
    const query = searchQuery.toLowerCase();
    return channels.filter(
      (channel) =>
        channel.name.toLowerCase().includes(query) ||
        channel.value?.toLowerCase().includes(query)
    );
  }, [channels, searchQuery]);

  // Handle channel click - navigate to channels page
  const handleChannelClick = useCallback(
    (channel: ChannelApiResponse & { value?: string }) => {
      if (!location.pathname.startsWith("/channels")) {
        navigate("/channels");
      }
    },
    [navigate, location.pathname]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && channels.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, channels.length]);

  // Get channel icon
  const getChannelIcon = (docType: string) => {
    switch (docType) {
      case "mail":
        return FaEnvelope;
      case "telegram":
        return FaTelegram;
      default:
        return FaComment;
    }
  };

  // Get channel icon color
  const getChannelColor = (docType: string) => {
    switch (docType) {
      case "mail":
        return "text-info";
      case "telegram":
        return "text-[#0088cc]";
      default:
        return "text-content-tertiary";
    }
  };

  if (channels.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)]">
        <span className="text-xs">Aucun channel</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaEnvelope size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Channels
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        <span className="text-[10px] text-[var(--arc-sidebar-text-secondary)]">
          {filteredChannels.length}
        </span>
      </div>

      {/* Search Bar */}
      {channels.length > 3 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Channels List */}
      <div className="space-y-0.5">
        {filteredChannels.map((channel) => {
          const ChannelIcon = getChannelIcon(channel.doc_type);
          const iconColor = getChannelColor(channel.doc_type);
          
          return (
            <button
              key={channel.id}
              onClick={() => handleChannelClick(channel)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
            >
              <ChannelIcon size={10} className={iconColor} />
              <span className="text-xs truncate flex-1">{channel.name}</span>
              <span className="text-[8px] px-1 py-0.5 bg-surface-secondary text-content-tertiary rounded capitalize">
                {channel.doc_type}
              </span>
            </button>
          );
        })}
      </div>

      {/* No results */}
      {filteredChannels.length === 0 && searchQuery && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      )}
    </div>
  );
};

ChannelsSection.displayName = "ChannelsSection";

export default ChannelsSection;
